import { Color } from "three"
var CustomShader = {
  uniforms: {
    texture1: { value: null },
    texture2: { value: null },
    disp: { value: null },
    effectFactor: { value: 0 },
    dispFactor: { value: 0 },
    scale: { value: 0 },
    amount: { value: 0 },
    opacity: { value: 1.0 },
    color: { value: new Color("white") },
  },


  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
    }
  `,

  fragmentShader: `
    varying vec2 vUv;
    uniform sampler2D texture1;
    uniform sampler2D texture2;
    uniform sampler2D disp;
    uniform sampler2D mask;
    uniform float _rot;
    uniform float dispFactor;
    uniform float effectFactor;
    uniform vec3 color;
    
    uniform float amount;
    uniform float angle;
    uniform float scale;
    uniform float opacity;
    
    uniform float glitch;
    uniform vec2 resolution; 
    uniform float time;    
    
    uniform float seed;
    uniform float seed_x;
    uniform float seed_y;
    uniform float distortion_x;
    uniform float distortion_y;
    uniform float col_s;
    

    vec4 chromatic(sampler2D tex) {
      vec2 p = (vUv - vec2(0.5, 0.5)) * (1.0 - scale) + vec2(0.5, 0.5);
      vec2 offset = amount / 18.0 * vec2(cos(angle), sin(angle));
      vec4 cr = texture2D(tex, p + offset);
      vec4 cga = texture2D(tex, p);
      vec4 cb = texture2D(tex, p - offset);
      return vec4(cr.r, cga.g, cb.b, cga.a); 
    }

    float rand(float n){return fract(sin(n) * 43758.5453123);}

    float rand2(vec2 co){ return fract(sin(dot(co.xy ,vec2(12.9898,78.233))) * 43758.5453);}

    float noise(float p){
      float fl = floor(p);
      float fc = fract(p);
      return mix(rand(fl), rand(fl + 1.0), fc);
    }

    float blockyNoise(vec2 uv, sampler2D texNoise , float threshold, float scale, float seed)
    {
      float scroll = floor(time + sin(11.0 *  time) + sin(time) ) * 0.77;
      vec2 noiseUV = uv.yy / scale + scroll;
      float noise2 = texture2D(texNoise, noiseUV).r;
      
      float id = floor( noise2 * 20.0);
      id = noise(id + seed) - 0.5;
      
    
      if ( abs(id) > threshold )
          id = 0.0;

      return id;
    }


    vec4 doGlitch3(sampler2D tDiffuse, sampler2D tDisp)
    {
      vec2 p = vUv;
      float xs = floor(gl_FragCoord.x / 0.5);
      float ys = floor(gl_FragCoord.y / 0.5);
      //based on staffantans glitch shader for unity https://github.com/staffantan/unityglitch
      vec4 normal = texture2D (tDisp, p*seed*seed);
      if(p.y<distortion_x+col_s && p.y>distortion_x-col_s*seed) 
      {
        if(seed_x>0.)
        {
          p.y = 1. - (p.y + distortion_y);
        }
        else 
        {
          p.y = distortion_y;
        }
      }
      if(p.x<distortion_y+col_s && p.x>distortion_y-col_s*seed) 
      {
        if(seed_y>0.)
        {
          p.x=distortion_x;
        }
        else 
        {
          p.x = 1. - (p.x + distortion_x);
        }
      }
      p.x+=normal.x*seed_x*(seed/5.);
      p.y+=normal.y*seed_y*(seed/5.);
      //base from RGB shift shader
      vec2 offset = amount * vec2( cos(angle), sin(angle));
      vec4 cr = texture2D(tDiffuse, p + offset);
      vec4 cga = texture2D(tDiffuse, p);
      vec4 cb = texture2D(tDiffuse, p - offset);
      gl_FragColor = vec4(cr.r, cga.g, cb.b, cga.a);
      //add noise
      vec4 snow = 200.*amount*vec4(rand2(vec2(xs * seed,ys * seed*50.))*0.05);
      gl_FragColor = gl_FragColor+ snow;
      return gl_FragColor;
    }


    vec4 doGlitch2(sampler2D tex, sampler2D texNoise)
    {
      float t = mod(time * 0.01, 0.04);
      vec2 uv = vUv ;
      vec2 block = floor(gl_FragCoord.xy / vec2(16));
      vec2 uv_noise = block / vec2(64);
      uv_noise += floor(vec2(t) * vec2(1234.0, 3543.0) / 2.1) / vec2(64);
 
      vec4 fragColor = doGlitch3(tex, texNoise);
        
      // interleave lines in some blocks  
      float line = fract(uv_noise.y / 0.3) * 3.0;
      vec3 mask = vec3(1.0, 0.9, 0.85);
      if (line > 0.333)
        mask = vec3(0.85, 1.0, 0.9);
      if (line > 0.666)
        mask = vec3(0.9, 0.85, 1.0);		
      
      fragColor.rgb *= mask;   
      fragColor.a *= opacity;  
      return fragColor;
    }

    float random(float x) {
      return fract(sin(x) * 10000.);
    }

    float noise2(vec2 p) {
      return random(p.x + p.y * 10000.);
    }
    float hash( vec2 p ) {
      float h = dot(p,vec2(127.1,311.7));	
      return fract(sin(h)*43758.5453123);
    }
    float noise( in vec2 p ) {
      vec2 i = floor( p );
      vec2 f = fract( p );	
      vec2 u = f*f*(3.0-2.0*f);
      return -1.0+2.0*mix( 
        mix( 
          hash( i + vec2(0.0,0.0) ), 
          hash( i + vec2(1.0,0.0) ), 
          u.x
        ),
        mix( 
          hash( i + vec2(0.0,1.0) ), 
          hash( i + vec2(1.0,1.0) ), 
          u.x
        ), 
        u.y
      );
    }


    vec2 sw(vec2 p) { return vec2(floor(p.x), floor(p.y)); }
    vec2 se(vec2 p) { return vec2(ceil(p.x), floor(p.y)); }
    vec2 nw(vec2 p) { return vec2(floor(p.x), ceil(p.y)); }
    vec2 ne(vec2 p) { return vec2(ceil(p.x), ceil(p.y)); }

    float smoothNoise(vec2 p) {
      vec2 interp = smoothstep(0., 1., fract(p));
      float s = mix(noise(sw(p)), noise(se(p)), interp.x);
      float n = mix(noise(nw(p)), noise(ne(p)), interp.x);
      return mix(s, n, interp.y);
    }

    float fractalNoise(vec2 p) {
      float x = 0.;
      x += smoothNoise(p      );
      x += smoothNoise(p * 2. ) / 2.;
      x += smoothNoise(p * 4. ) / 4.;
      x += smoothNoise(p * 8. ) / 8.;
      x += smoothNoise(p * 16.) / 16.;
      x /= 1. + 1./2. + 1./4. + 1./8. + 1./16.;
      return x;
    }

    float movingNoise(vec2 p) {
      float x = fractalNoise(p + time) * 5.;
      float y = fractalNoise(p - time);
      return fractalNoise(p + vec2(x, y));   
    }

    // call this for water noise function
    float nestedNoise(vec2 p) {
      float x = movingNoise(p);
      float y = movingNoise(p + 100.);
      return movingNoise(p + vec2(x, y));  
    }
    ////////

    const int NUM_STEPS = 8;
    const float PI	 	= 3.141592;
    const float EPSILON	= 1e-3;
    #define EPSILON_NRM (0.1 / 1000.0)
    #define AA

    // sea
    const int ITER_GEOMETRY = 1;
    const int ITER_FRAGMENT = 4;
    const float SEA_HEIGHT = 0.5;
    const float SEA_CHOPPY = 5.0;
    const float SEA_SPEED = 0.6;
    const float SEA_FREQ = 0.2;
    const vec3 SEA_BASE = vec3(0.0,0.09,0.18);
    const vec3 SEA_WATER_COLOR = vec3(0.8,0.9,0.6)*0.6;
    #define SEA_TIME (1.0 + time * SEA_SPEED)
    const mat2 octave_m = mat2(1.6,1.2,-1.2,1.6);

    // math
    mat3 fromEuler(vec3 ang) {
      vec2 a1 = vec2(sin(ang.x),cos(ang.x));
      vec2 a2 = vec2(sin(ang.y),cos(ang.y));
      vec2 a3 = vec2(sin(ang.z),cos(ang.z));
      mat3 m;
      m[0] = vec3(a1.y*a3.y+a1.x*a2.x*a3.x,a1.y*a2.x*a3.x+a3.y*a1.x,-a2.y*a3.x);
      m[1] = vec3(-a2.y*a1.x,a1.y*a2.y,a2.x);
      m[2] = vec3(a3.y*a1.x*a2.x+a1.y*a3.x,a1.x*a3.x-a1.y*a3.y*a2.x,a2.y*a3.y);
      return m;
    }

    /*
    float hash( vec2 p ) {
      float h = dot(p,vec2(127.1,311.7));	
      return fract(sin(h)*43758.5453123);
    }
    float noise( in vec2 p ) {
      vec2 i = floor( p );
      vec2 f = fract( p );	
      vec2 u = f*f*(3.0-2.0*f);
      
      return -1.0+2.0*mix( 
        mix( 
          hash( i + vec2(0.0,0.0) ), 
          hash( i + vec2(1.0,0.0) ), 
          u.x
        ),
        mix( 
          hash( i + vec2(0.0,1.0) ), 
          hash( i + vec2(1.0,1.0) ), 
          u.x
        ), 
        u.y
      );
    }
    */

    // lighting
    float diffuse(vec3 n,vec3 l,float p) {
      return pow(dot(n,l) * 0.4 + 0.6,p);
    }
    float specular(vec3 n,vec3 l,vec3 e,float s) {    
      float nrm = (s + 8.0) / (PI * 8.0);
      return pow(max(dot(reflect(e,n),l),0.0),s) * nrm;
    }

    // sky
    vec3 getSkyColor(vec3 e) {
      e.y = (max(e.y,0.0)*0.8+0.2)*0.8;
      return vec3(pow(1.0-e.y,2.0), 1.0-e.y, 0.6+(1.0-e.y)*0.4) * 1.1;
    }

    // sea
    float sea_octave(vec2 uv, float choppy) {
      uv += noise(uv);        
      vec2 wv = 1.0-abs(sin(uv));
      vec2 swv = abs(cos(uv));    
      wv = mix(wv,swv,wv);
      return pow(1.0-pow(wv.x * wv.y,0.85),choppy);
    }

    float map(vec3 p) {
      float freq = SEA_FREQ;
      float amp = SEA_HEIGHT;
      float choppy = SEA_CHOPPY;
      vec2 uv = p.xz; uv.x *= 0.75;
      
      float d, h = 0.0;    
      for(int i = 0; i < ITER_GEOMETRY; i++) {        
        d = sea_octave((uv+SEA_TIME)*freq,choppy);
        d += sea_octave((uv-SEA_TIME)*freq,choppy);
        h += d * amp;        
        uv *= octave_m; freq *= 1.9; amp *= 0.22;
        choppy = mix(choppy,1.0,0.2);
      }
      return p.y - h;
    }

    float map_detailed(vec3 p) {
        float freq = SEA_FREQ;
        float amp = SEA_HEIGHT;
        float choppy = SEA_CHOPPY;
        vec2 uv = p.xz; uv.x *= 0.75;

        float d, h = 0.0;    
        for(int i = 0; i < ITER_FRAGMENT; i++) {        
          d = sea_octave((uv+SEA_TIME)*freq,choppy);
          d += sea_octave((uv-SEA_TIME)*freq,choppy);
          h += d * amp;        
          uv *= octave_m; freq *= 1.9; amp *= 0.22;
          choppy = mix(choppy,1.0,0.2);
        }
        return p.y - h;
    }

    vec3 getSeaColor(vec3 p, vec3 n, vec3 l, vec3 eye, vec3 dist) {  
      float fresnel = clamp(1.0 - dot(n,-eye), 0.0, 1.0);
      fresnel = pow(fresnel,3.0) * 0.5;

      vec3 reflected = getSkyColor(reflect(eye,n));    
      vec3 refracted = SEA_BASE + diffuse(n,l,80.0) * texture2D(texture2, vUv).xyz * 0.12; 

      vec3 color = mix(refracted,reflected,fresnel);
      float atten = max(1.0 - dot(dist,dist) * 0.001, 0.0);
      color += SEA_WATER_COLOR * (p.y - SEA_HEIGHT) * 0.18 * atten;
      color += vec3(specular(n,l,eye,60.0)) * vec3(0.872, 0.692, 0.521);

      return color;
    }

    // tracing
    vec3 getNormal(vec3 p, float eps) {
      vec3 n;
      n.y = map_detailed(p);    
      n.x = map_detailed(vec3(p.x+eps,p.y,p.z)) - n.y;
      n.z = map_detailed(vec3(p.x,p.y,p.z+eps)) - n.y;
      n.y = eps;
      return normalize(n);
    }

    float heightMapTracing(vec3 ori, vec3 dir, out vec3 p) {  
      float tm = 0.0;
      float tx = 1000.0;    
      float hx = map(ori + dir * tx);
      if(hx > 0.0) return tx;   
      float hm = map(ori + dir * tm);    
      float tmid = 0.0;
      for(int i = 0; i < NUM_STEPS; i++) 
      {
        tmid = mix(tm,tx, hm/(hm-hx));                   
        p = ori + dir * tmid;                   
        float hmid = map(p);
        if(hmid < 0.0) {
          tx = tmid;
          hx = hmid;
        } else {
          tm = tmid;
          hm = hmid;
        }
      }
      return tmid;
    }

    vec3 getPixel(in vec2 coord, float time) {    
          vec2 uv = vUv;
        uv = uv * 15.0 - 7.0;

        // ray
        vec3 ang = vec3(0.2,0.1,0.7);   
        vec3 ori = vec3(0,6.,0);
        vec3 dir = normalize(vec3(uv.xy,-2.0)); dir.z += length(uv) * 0.14;
        dir = normalize(dir) * fromEuler(ang);
        
        // tracing
        vec3 p;
        heightMapTracing(ori,dir,p);
        vec3 dist = p - ori;
        vec3 n = getNormal(p, dot(dist,dist) * EPSILON_NRM);
        vec3 light = normalize(vec3(0.0,1.0,0.8)); 

        // color
        return mix(
          getSkyColor(dir),
          getSeaColor(p,n,light,dir,dist),
          pow(smoothstep(0.0,-0.02,dir.y),0.2)
        );
    }
    ////////

    void main() {
      
      if (glitch == 1.0) {
        gl_FragColor = doGlitch2(texture1, disp);
      } else if (dispFactor == 0.0) {
        vec4 finalTexture = texture2D(texture1, vUv);
        finalTexture.a *= opacity; 
        gl_FragColor = finalTexture;  
      } else {
        vec2 uv = vUv;
        vec4 disp4 = texture2D(texture2, uv);

        vec2 distortedPosition = vec2(uv.x + dispFactor * (disp4.r*effectFactor), uv.y);
        vec2 distortedPosition2 = vec2(uv.x - (1.0 - dispFactor) * (disp4.r*effectFactor), uv.y);

        vec4 _texture = texture2D(texture1, distortedPosition);
        vec4 _texture2 = texture2D(texture2, distortedPosition2);

        vec4 finalTexture = mix(_texture, _texture2, dispFactor);
        
        if (dispFactor > 0.5) {
          vec4 mask = texture2D(mask, uv); 
          float alpha = mask.r;
          if ( alpha > .0) {
            float iTime = time * 0.03;
            vec2 fragCoord = gl_FragCoord.xy;
            vec3 color = getPixel(fragCoord, iTime);
            color.r = clamp(color.r,0.,1.);
            color.g = clamp(color.g,0.,1.);
            color.b = clamp(color.b,0.,1.);     
            // post
            vec4 waterCol = vec4(pow(color,vec3(0.65)), 1.0);
            gl_FragColor = vec4(mix(finalTexture.xyz, waterCol.xyz, alpha * 0.2), opacity);
            return;
          }      
        }   
        finalTexture.a *= opacity; 
        gl_FragColor = finalTexture;           
      }
    }
  `,
}

export { CustomShader }
